"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeDigest = exports.getManifest = exports.getAttestationManifest = void 0;
const registry_call_1 = require("./registry-call");
const contentTypes = require("./content-types");
const needle_1 = require("./needle");
const validation_1 = require("./validation");
const crypto_1 = require("crypto");
const digestRegex = /^sha256:[a-f0-9]{64}$/i;
const referenceType = "vnd.docker.reference.type";
const attestationManifestType = "attestation-manifest";
/**
 * Pulls a attestation manifest for platform.
 *
 * OCI Distribution Specification endpoint: `/v2/<name>/manifests/<reference>`
 *
 * @param platform The platform to retrieve if the manifest is multi-architecture. (Defaults to linux/amd64).
 * @returns Returns manifest.
 * @throws Throws when platform in multi-architecture manifest cannot be found.
 */
async function getAttestationManifest(registryBase, repo, imageReference, username, password, options = {}, platform, callChainObserver = { visitedUrls: [] }) {
    return getManifestHelper(registryBase, repo, imageReference, findAttestationManifest, [contentTypes.MANIFEST_LIST_V2, contentTypes.OCI_INDEX_V1], username, password, Object.assign(Object.assign({}, options), { acceptManifest: `${contentTypes.MANIFEST_V2}, ${contentTypes.MANIFEST_LIST_V2}, ${contentTypes.OCI_MANIFEST_V1}, ${contentTypes.OCI_INDEX_V1}` }), platform, callChainObserver);
}
exports.getAttestationManifest = getAttestationManifest;
/**
 * Pulls a manifest for platform.
 *
 * OCI Distribution Specification endpoint: `/v2/<name>/manifests/<reference>`
 *
 * @param platform The platform to retrieve if the manifest is multi-architecture. (Defaults to linux/amd64).
 * @returns Returns manifest.
 * @throws Throws when platform in multi-architecture manifest cannot be found.
 */
async function getManifest(registryBase, repo, imageReference, username, password, options = {}, platform, callChainObserver = { visitedUrls: [] }) {
    return getManifestHelper(registryBase, repo, imageReference, getManifestDigestByPlatform, undefined, username, password, options, platform, callChainObserver);
}
exports.getManifest = getManifest;
async function getManifestHelper(registryBase, repo, imageReference, manifestDigestExtractor, expectedContentTypes, username, password, options = {}, platform, callChainObserver = { visitedUrls: [] }) {
    var _a;
    const accept = (_a = options.acceptManifest) !== null && _a !== void 0 ? _a : `${contentTypes.MANIFEST_V2}, ${contentTypes.MANIFEST_LIST_V2}`;
    const endpoint = `/${repo}/manifests/${imageReference}`;
    try {
        (0, validation_1.validateRepoOrImageRef)(repo);
        (0, validation_1.validateRepoOrImageRef)(imageReference);
    }
    catch (err) {
        throw new needle_1.NeedleWrapperException(err.message, 400);
    }
    let manifestResponse = await (0, registry_call_1.registryV2Call)(registryBase, endpoint, accept, callChainObserver, username, password, options);
    const contentType = manifestResponse.headers["content-type"];
    if ((expectedContentTypes === null || expectedContentTypes === void 0 ? void 0 : expectedContentTypes.length) &&
        !expectedContentTypes.includes(contentType)) {
        return null;
    }
    let indexDigest;
    let manifestDigest;
    if (contentType === contentTypes.MANIFEST_LIST_V2 ||
        contentType === contentTypes.OCI_INDEX_V1) {
        indexDigest = computeDigest(manifestResponse.body);
        manifestDigest = manifestDigestExtractor(manifestResponse, platform, {
            imageRegistry: registryBase,
            imageRepository: repo,
            imageReference,
        });
        // need to call again with actual manifest (and not a list of manifest-lists)
        const endpoint = `/${repo}/manifests/${manifestDigest}`;
        manifestResponse = await (0, registry_call_1.registryV2Call)(registryBase, endpoint, accept, callChainObserver !== null && callChainObserver !== void 0 ? callChainObserver : { visitedUrls: [] }, username, password, options);
    }
    return processResponse(manifestResponse, imageReference, manifestDigest, indexDigest);
}
function processResponse(manifestResponse, imageReference, manifestDigest, indexDigest) {
    const dockerContentDigest = manifestResponse.headers["Docker-Content-Digest"];
    switch (true) {
        case manifestDigest != undefined:
            break;
        case isManifestDigest(imageReference):
            manifestDigest = imageReference;
            break;
        case isManifestDigest(dockerContentDigest):
            manifestDigest = dockerContentDigest;
            break;
        default:
            manifestDigest = computeDigest(manifestResponse.body);
    }
    const manifestContentType = manifestResponse.headers["content-type"];
    const parsedBody = (0, needle_1.parseResponseBody)(manifestResponse);
    return Object.assign(Object.assign({}, parsedBody), { indexDigest, manifestDigest, manifestContentType });
}
function isManifestDigest(imageReference) {
    return digestRegex.test(imageReference);
}
function computeDigest(body) {
    if (!body) {
        return undefined;
    }
    if (typeof body !== "string") {
        body = JSON.stringify(body);
    }
    const hexDigest = (0, crypto_1.createHash)("sha256")
        .update(body)
        .digest("hex")
        .toLowerCase();
    return `sha256:${hexDigest}`;
}
exports.computeDigest = computeDigest;
function findAttestationManifest(manifestResponse) {
    const manifestList = (0, needle_1.parseResponseBody)(manifestResponse);
    const attestationManifest = manifestList.manifests.find(manifest => { var _a; return ((_a = manifest.annotations) === null || _a === void 0 ? void 0 : _a[referenceType]) == attestationManifestType; });
    if (attestationManifest) {
        return attestationManifest.digest;
    }
    throw new Error("could not find attestation manifest");
}
function getManifestDigestByPlatform(manifestResponse, platform, logAttributes) {
    const manifestList = (0, needle_1.parseResponseBody)(manifestResponse);
    const filteredManifestList = manifestList.manifests.filter(manifest => {
        return (manifest.platform.architecture !== "unknown" ||
            manifest.platform.os !== "unknown");
    });
    if (filteredManifestList.length === 1) {
        // If there is only one manifest, we can extract the digest directly
        return filteredManifestList[0].digest;
    }
    manifestList.manifests = filteredManifestList;
    // Otherwise, we need to find the manifest for the specified/default platform
    const defaultPlatform = {
        os: "linux",
        architecture: "amd64",
    };
    const platformFilter = platform ? platform : defaultPlatform;
    const manifestPlatform = getManifestByOsAndArch(manifestList.manifests, platformFilter.os, platformFilter.architecture, platformFilter.variant);
    if (manifestPlatform) {
        return manifestPlatform.digest;
    }
    throw new Error(`no supported manifest found for platform: ${JSON.stringify(platformFilter)}`);
}
function getManifestByOsAndArch(platformManifest, os, architecture, variant) {
    return platformManifest.find(manifest => {
        var _a, _b, _c;
        return ((_a = manifest.platform) === null || _a === void 0 ? void 0 : _a.os) === os &&
            ((_b = manifest.platform) === null || _b === void 0 ? void 0 : _b.architecture) === architecture &&
            ((_c = manifest.platform) === null || _c === void 0 ? void 0 : _c.variant) === variant;
    });
}
//# sourceMappingURL=get-manifest.js.map